import React, { useState } from 'react';
import { usePromocodeList } from '@/hooks/queries/promocode/useList';
import { usePromocodeCreate, usePromocodeUpdate, usePromocodeDelete } from '@/hooks/mutations/promocode';
import { IPromocode, DiscountEnum, IPromocodeInput } from '@/types/promocode';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { AlertDialog } from '@/components/AlertDialog';
import Loader from '@/components/Loader';
import { Plus, Search, Edit2, Trash2, TicketPercent } from 'lucide-react';
import { useToast } from '@/components/ui/use-toast';
import { Pagination } from '@/components/Pagination';

const PromocodesPage = () => {
  const { toast } = useToast();
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);
  const [selectedPromocode, setSelectedPromocode] = useState<IPromocode | null>(null);

  const [formData, setFormData] = useState<IPromocodeInput>({
    code: '',
    discountType: DiscountEnum.PERCENT,
    discountValue: 10,
    maxUses: 100,
    userLimit: 1,
    minOrderValue: 0,
    startDate: '',
    endDate: '',
    courseIds: [],
    planIds: [],
  });

  const { data: promocodesData, isLoading, refetch } = usePromocodeList({ search, pageNumber: page, pageSize: 20 });
  const { mutate: createPromocode, isPending: isCreating } = usePromocodeCreate();
  const { mutate: updatePromocode, isPending: isUpdating } = usePromocodeUpdate();
  const { mutate: deletePromocode } = usePromocodeDelete();

  const handleOpenCreate = () => {
    setSelectedPromocode(null);
    setFormData({
      code: '',
      discountType: DiscountEnum.PERCENT,
      discountValue: 10,
      maxUses: 100,
      userLimit: 1,
      minOrderValue: 0,
      startDate: '',
      endDate: '',
      courseIds: [],
      planIds: [],
    });
    setIsModalOpen(true);
  };

  const handleOpenEdit = (promo: IPromocode) => {
    setSelectedPromocode(promo);
    setFormData({
      code: promo.code,
      discountType: promo.discountType,
      discountValue: promo.discountValue,
      maxUses: promo.maxUses || 100,
      userLimit: promo.userLimit || 1,
      minOrderValue: promo.minOrderValue || 0,
      startDate: promo.startDate ? promo.startDate.slice(0, 10) : '',
      endDate: promo.endDate ? promo.endDate.slice(0, 10) : '',
      courseIds: promo.courseIds || [],
      planIds: promo.planIds || [],
    });
    setIsModalOpen(true);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const payload = {
      ...formData,
      startDate: formData.startDate ? new Date(formData.startDate).toISOString() : undefined,
      endDate: formData.endDate ? new Date(formData.endDate).toISOString() : undefined,
    };

    if (selectedPromocode) {
      updatePromocode(
        { id: selectedPromocode.id, data: payload },
        {
          onSuccess: () => {
            toast({ title: 'Muvaffaqiyatli', description: 'Promokod yangilandi' });
            setIsModalOpen(false);
            refetch();
          },
          onError: () => toast({ title: 'Xatolik', description: 'Promokod yangilanmadi', variant: 'destructive' }),
        }
      );
    } else {
      createPromocode(payload, {
        onSuccess: () => {
          toast({ title: 'Muvaffaqiyatli', description: 'Yangi promokod yaratildi' });
          setIsModalOpen(false);
          refetch();
        },
        onError: () => toast({ title: 'Xatolik', description: 'Promokod yaratilmadi', variant: 'destructive' }),
      });
    }
  };

  const handleDelete = () => {
    if (!selectedPromocode) return;
    deletePromocode(selectedPromocode.id, {
      onSuccess: () => {
        toast({ title: 'Muvaffaqiyatli', description: "Promokod o'chirildi" });
        setIsDeleteOpen(false);
        setSelectedPromocode(null);
        refetch();
      },
      onError: () => toast({ title: 'Xatolik', description: "Promokod o'chirilmadi", variant: 'destructive' }),
    });
  };

  const promocodesList: IPromocode[] = promocodesData?.data?.data || promocodesData?.data || [];

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 tracking-tight">Promokodlar</h1>
          <p className="text-sm text-slate-500 mt-1">Kurslar va obuna rejalari uchun chegirma kodlari</p>
        </div>
        <Button onClick={handleOpenCreate} className="gap-2 bg-primary hover:opacity-90">
          <Plus className="size-4" /> Yangi promokod
        </Button>
      </div>

      <div className="bg-white rounded-2xl border border-border shadow-sm overflow-hidden p-6 space-y-6">
        <div className="flex items-center justify-between gap-4">
          <div className="relative w-full max-w-sm">
            <Search className="absolute left-3.5 top-3 size-4 text-slate-400" />
            <Input
              placeholder="Promokod kodi bo'yicha qidirish..."
              value={search}
              onChange={(e) => { setSearch(e.target.value); setPage(1); }}
              className="pl-10 h-10 bg-slate-50/50 border-slate-200 rounded-xl"
            />
          </div>
        </div>

        {isLoading ? (
          <div className="py-12"><Loader /></div>
        ) : promocodesList.length === 0 ? (
          <div className="text-center py-12 text-slate-500">
            <TicketPercent className="size-12 mx-auto text-slate-300 mb-3" />
            <p>Hozircha promokodlar mavjud emas</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left text-sm text-slate-600">
              <thead className="bg-slate-50/75 text-xs uppercase font-semibold text-slate-500 border-b border-slate-100">
                <tr>
                  <th className="px-4 py-3.5">Kod</th>
                  <th className="px-4 py-3.5">Chegirma Turi</th>
                  <th className="px-4 py-3.5">Qiymati</th>
                  <th className="px-4 py-3.5">Limit</th>
                  <th className="px-4 py-3.5">Amal Qilish Muddati</th>
                  <th className="px-4 py-3.5 text-right">Amallar</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {promocodesList.map((promo) => (
                  <tr key={promo.id} className="hover:bg-slate-50/50 transition-colors">
                    <td className="px-4 py-3 font-mono font-bold text-slate-900">
                      <span className="px-2.5 py-1 bg-slate-100 rounded-lg text-primary">{promo.code}</span>
                    </td>
                    <td className="px-4 py-3">
                      <Badge variant="outline" className="font-medium text-xs">
                        {promo.discountType === DiscountEnum.PERCENT ? 'Foiz (%)' : 'Aniq summa (so\'m)'}
                      </Badge>
                    </td>
                    <td className="px-4 py-3 font-semibold text-slate-900">
                      {promo.discountType === DiscountEnum.PERCENT
                        ? `${promo.discountValue}%`
                        : `${promo.discountValue?.toLocaleString()} so'm`}
                    </td>
                    <td className="px-4 py-3 text-slate-600">{promo.maxUses || 'Cheksiz'}</td>
                    <td className="px-4 py-3 text-slate-500 text-xs">
                      {promo.startDate?.slice(0, 10)} — {promo.endDate?.slice(0, 10) || 'Cheksiz'}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <div className="flex items-center justify-end gap-1">
                        <Button variant="ghost" size="icon" onClick={() => handleOpenEdit(promo)} className="size-8 text-slate-600 hover:text-primary">
                          <Edit2 className="size-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => {
                            setSelectedPromocode(promo);
                            setIsDeleteOpen(true);
                          }}
                          className="size-8 text-slate-600 hover:text-red-500"
                        >
                          <Trash2 className="size-4" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
<Pagination className="mt-4" currentPage={page} setCurrentPage={setPage} paginationInfo={promocodesData?.meta?.pagination ?? promocodesData?.data?.meta?.pagination} />
          </div>
        )}
      </div>

      <Dialog open={isModalOpen} onOpenChange={setIsModalOpen}>
        <DialogContent className="max-w-xl bg-white rounded-2xl p-6">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold text-slate-900">
              {selectedPromocode ? 'Promokodni tahrirlash' : 'Yangi promokod yaratish'}
            </DialogTitle>
          </DialogHeader>

          <form onSubmit={handleSubmit} className="space-y-4 mt-2">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label className="text-xs font-semibold text-slate-700">Promokod (KOD)</Label>
                <Input
                  placeholder="SUMMER20"
                  value={formData.code}
                  onChange={(e) => setFormData({ ...formData, code: e.target.value.toUpperCase() })}
                  className="rounded-xl font-mono uppercase"
                  required
                />
              </div>

              <div className="space-y-1.5">
                <Label className="text-xs font-semibold text-slate-700">Chegirma turi</Label>
                <Select
                  value={formData.discountType}
                  onValueChange={(val) => setFormData({ ...formData, discountType: val as DiscountEnum })}
                >
                  <SelectTrigger className="rounded-xl">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-white rounded-xl shadow-lg border border-border">
                    <SelectItem value={DiscountEnum.PERCENT}>Foiz (%)</SelectItem>
                    <SelectItem value={DiscountEnum.FIXED}>Aniq summa (so'm)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid grid-cols-3 gap-4">
              <div className="space-y-1.5">
                <Label className="text-xs font-semibold text-slate-700">Qiymati</Label>
                <Input
                  type="number"
                  value={formData.discountValue}
                  onChange={(e) => setFormData({ ...formData, discountValue: Number(e.target.value) })}
                  className="rounded-xl"
                  required
                />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs font-semibold text-slate-700">Max foydalanish</Label>
                <Input
                  type="number"
                  value={Number(formData.maxUses) || 100}
                  onChange={(e) => setFormData({ ...formData, maxUses: Number(e.target.value) })}
                  className="rounded-xl"
                />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs font-semibold text-slate-700">User limit</Label>
                <Input
                  type="number"
                  value={Number(formData.userLimit) || 1}
                  onChange={(e) => setFormData({ ...formData, userLimit: Number(e.target.value) })}
                  className="rounded-xl"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label className="text-xs font-semibold text-slate-700">Boshlanish sanasi</Label>
                <Input
                  type="date"
                  value={formData.startDate || ''}
                  onChange={(e) => setFormData({ ...formData, startDate: e.target.value })}
                  className="rounded-xl"
                />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs font-semibold text-slate-700">Tugash sanasi</Label>
                <Input
                  type="date"
                  value={formData.endDate || ''}
                  onChange={(e) => setFormData({ ...formData, endDate: e.target.value })}
                  className="rounded-xl"
                />
              </div>
            </div>

            <DialogFooter className="pt-4 border-t border-slate-100">
              <Button type="button" variant="outline" onClick={() => setIsModalOpen(false)} className="rounded-xl">
                Bekor qilish
              </Button>
              <Button type="submit" disabled={isCreating || isUpdating} className="rounded-xl bg-primary text-white">
                {isCreating || isUpdating ? 'Saqlanmoqda...' : 'Saqlash'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <AlertDialog
        isOpen={isDeleteOpen}
        setIsOpen={setIsDeleteOpen}
        alertTitle="Promokodni o'chirishni tasdiqlaysizmi?"
        alertDescription="Ushbu promokod tizimdan o'chiriladi."
        alertActionTitle="O'chirish"
        alertCancel="Bekor qilish"
        alertActionFunction={handleDelete}
      />
    </div>
  );
};

export default PromocodesPage;
